import React from 'react';
import { TrendingUp, Clock, Megaphone, ArrowRight } from 'lucide-react';
import type { BlockRenderProps } from './types';

export const StatsCounterRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const headline = (node.props?.headline as string) || 'Our Legacy in Numbers';
  const subheadline = (node.props?.subheadline as string) || 'Consistent results year after year across national competitive benchmarks.';

  const stat1_value = (node.props?.stat1_value as string) || '99.8%';
  const stat1_label = (node.props?.stat1_label as string) || 'Top JEE & NEET Percentile';
  const stat2_value = (node.props?.stat2_value as string) || '2,450+';
  const stat2_label = (node.props?.stat2_label as string) || 'Selections in Govt Colleges';
  const stat3_value = (node.props?.stat3_value as string) || '48+';
  const stat3_label = (node.props?.stat3_label as string) || 'AIR Top 100 Ranks';
  const stat4_value = (node.props?.stat4_value as string) || '16+';
  const stat4_label = (node.props?.stat4_label as string) || 'Years of Pedagogy Excellence';

  const stats = [
    { value: stat1_value, label: stat1_label },
    { value: stat2_value, label: stat2_label },
    { value: stat3_value, label: stat3_label },
    { value: stat4_value, label: stat4_label },
  ];

  return (
    <div className="py-8 w-full text-center">
      <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
        <TrendingUp className="h-3.5 w-3.5" />
        <span>Performance Metrics</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2">
        {headline}
      </h2>
      {subheadline && (
        <p className="text-xs md:text-sm text-muted-foreground max-w-lg mx-auto mb-8 leading-relaxed">
          {subheadline}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
        {stats.map((s, idx) => (
          <div
            key={idx}
            className="p-5 rounded-2xl bg-card border border-border shadow-xs hover:border-primary/40 transition-all flex flex-col items-center justify-center"
          >
            <div className="text-2xl md:text-4xl font-black text-primary tracking-tight">
              {s.value}
            </div>
            <div className="text-xs font-semibold text-muted-foreground mt-1.5 leading-snug">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CountdownTimerRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const headline = (node.props?.headline as string) || 'Next Batch Admissions Closing Soon';
  const subheadline = (node.props?.subheadline as string) || 'Last few seats remaining for JEE & NEET 2026/2027 entrance batches.';
  const days = (node.props?.days as string) || '04';
  const hours = (node.props?.hours as string) || '18';
  const minutes = (node.props?.minutes as string) || '35';
  const seconds = (node.props?.seconds as string) || '40';
  const ctaText = (node.props?.ctaText as string) || 'Reserve Your Seat Now';
  const ctaLink = (node.props?.ctaLink as string) || '#admissions';

  const timeUnits = [
    { label: 'DAYS', val: days },
    { label: 'HOURS', val: hours },
    { label: 'MINUTES', val: minutes },
    { label: 'SECONDS', val: seconds },
  ];

  return (
    <div className="py-6 px-4 md:px-8 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-muted border border-primary/20 text-center my-4 shadow-sm">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider mb-3">
        <Clock className="h-3.5 w-3.5" />
        <span>Admission Deadline</span>
      </div>
      <h3 className="text-xl md:text-2xl font-extrabold text-foreground mb-1.5">
        {headline}
      </h3>
      <p className="text-xs text-muted-foreground max-w-md mx-auto mb-6">
        {subheadline}
      </p>

      {/* Timer Units */}
      <div className="flex items-center justify-center gap-2 sm:gap-4 mb-6">
        {timeUnits.map((u, i) => (
          <div key={i} className="flex flex-col items-center">
            <div className="w-14 sm:w-18 h-14 sm:h-18 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center text-xl sm:text-2xl font-black text-foreground font-mono">
              {u.val}
            </div>
            <span className="text-[10px] font-bold text-muted-foreground mt-1 tracking-wider">
              {u.label}
            </span>
          </div>
        ))}
      </div>

      <a
        href={ctaLink}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs sm:text-sm font-bold shadow hover:opacity-90 transition-opacity"
      >
        <span>{ctaText}</span>
        <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
};

export const CallToActionBannerRenderer: React.FC<BlockRenderProps> = ({ node }) => {
  const headline = (node.props?.headline as string) || 'Ready to Start Your Journey to a Top Rank?';
  const subheadline = (node.props?.subheadline as string) || 'Register today for our diagnostic scholarship test and claim up to 90% fee waiver.';
  const ctaText = (node.props?.ctaText as string) || 'Apply For Scholarship Test';
  const ctaLink = (node.props?.ctaLink as string) || '/admissions#scholarship';
  const variant = (node.props?.variant as string) || 'primary';

  const isDark = variant === 'dark';

  return (
    <div
      className={`py-8 px-6 md:px-12 rounded-3xl my-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md ${
        isDark
          ? 'bg-slate-900 text-white border border-slate-800'
          : 'bg-primary text-primary-foreground'
      }`}
    >
      <div className="max-w-xl text-center md:text-left">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-xs font-bold uppercase tracking-wider mb-2 backdrop-blur-xs">
          <Megaphone className="h-3.5 w-3.5" />
          <span>Scholarship Alert</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-black tracking-tight leading-tight mb-2">
          {headline}
        </h2>
        <p className="text-xs md:text-sm opacity-90 leading-relaxed">
          {subheadline}
        </p>
      </div>

      <div className="shrink-0">
        <a
          href={ctaLink}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-slate-900 font-extrabold text-sm shadow hover:bg-white/90 transition-all"
        >
          <span>{ctaText}</span>
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
};
